import React, { useEffect, useMemo, useRef } from 'react';
import { Label } from '@progress/kendo-react-labels';
import { Icon } from '@progress/kendo-react-common';
import MultiSelect from 'shared/inputs/BaseMultiSelect';
import Button from 'shared/buttons';

const defaultSortByList = [];
const popupSettings = { width: 325 };

const tagRender = (tagData, li) => {
  const { data } = tagData;
  const [dataItem] = data;

  return React.cloneElement(li, li.props, [
    <span key={tagData.text}>
      {tagData.text}
      <Icon name={dataItem.dir === 'asc' ? 'arrow-up' : 'arrow-down'} />
    </span>,
    li.props.children,
  ]);
};

const MultiSortSelect = ({
  sortByList = defaultSortByList, onSortChange, sort, defaultSort,
}) => {
  const options = useMemo(() => sortByList.map((sortItem) => {
    const { field } = sortItem;

    // Find item sorting index if selected
    const selectedInd = sort.findIndex((s) => s.field === field);

    if (selectedInd < 0) {
      return { ...sortItem, isSelected: false };
    }

    const selected = sort[selectedInd];

    return {
      ...sortItem,
      isSelected: true,
      dir: selected.dir,
      order: selectedInd + 1,
    };
  }), [sortByList, sort]);

  const sortRef = useRef(sort);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  const value = useMemo(() => sort
    .map((s) => {
      const item = options.find((o) => o.field === s.field);
      if (!item) {
        return null;
      }
      return item;
    })
    .filter((item) => !!item), [options, sort]);

  // Change set of sorting fields
  const onSortFieldSelect = ({ value }) => {
    const updated = value && value.length > 0 ? value : defaultSort;
    const sort = updated.map(({ field, dir }) => ({ field, dir: dir ?? 'asc' }));
    onSortChange({ sort });
  };

  // Toggle sorting direction
  const toggleDir = (sortItem) => {
    const updated = [...sortRef.current];
    const ind = updated.findIndex(({ field }) => field === sortItem.field);

    if (ind < 0) {
      return;
    }

    updated[ind] = { ...sortItem, dir: sortItem.dir === 'asc' ? 'desc' : 'asc' };
    onSortFieldSelect({ value: updated });
  };

  // Dropdown options list item
  const itemRender = useMemo(() => (li, itemProps) => {
    const { dataItem } = itemProps;

    const {
      title, order, dir, isSelected,
    } = dataItem;

    const itemChildren = (
      <div className='toolbar-multi-sort-select-item'>
        <div className='select-item-title'>
          {isSelected && <div className='select-item-order'>{order}</div>}
          <div className='select-item-name'>{title}</div>
        </div>
        {isSelected && (
          <Button
            size='small'
            rounded='full'
            fillMode='flat'
            themeColor='light'
            icon={dir === 'asc' ? 'arrow-up' : 'arrow-down'}
            onClick={(e) => {
              e.stopPropagation();
              toggleDir(dataItem);
            }}
          />
        )}
      </div>
    );

    return React.cloneElement(
      li,
      { ...li.props, className: `${li.props.className} multi-sort-list-item` },
      itemChildren,
    );
  }, []);

  return (
    <div className='toolbar-multi-sort-select-container'>
      <Label>Sort By</Label>
      <div className='sort-fields'>
        <MultiSelect
          data={options}
          value={value}
          onChange={onSortFieldSelect}
          textField='title'
          dataItemKey='field'
          itemRender={itemRender}
          popupSettings={popupSettings}
          tagRender={tagRender}
        />
      </div>
    </div>
  );
};

export default MultiSortSelect;
